import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCheckout } from "@/lib/checkout";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, ShoppingCart } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import { useCurrency } from "@/context/CurrencyContext";
import { useTr } from "@/i18n/translator";

export default function BrandService() {
  const { user, loading: authLoading } = useAuth();
  const { start, loading: checkoutLoading } = useCheckout();
  const [orders, setOrders] = useState([]);
  const [withEcom, setWithEcom] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tr = useTr();
  const { format } = useCurrency();

  const sku = withEcom ? "brand_service_ecom" : "brand_service";
  const priceUsd = withEcom ? 328 : 299;

  useEffect(() => {
    if (user) {
      api.get("/billing/brand-orders").then(({ data }) => setOrders(data || [])).catch(() => {});
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0A0A0A]">
      <header className="sticky top-0 z-40 bg-[#F4F4F0] border-b border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-service-logo">
            <img src="/logo-cia.png" alt="CIA Marketing" className="w-10 h-10 object-contain" />
            <span className="font-display text-lg">CIA Marketing<span className="text-[#E53935]">®</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <CurrencySwitcher />
            {user ? (
              <Link to="/dashboard" className="font-mono-mark text-[11px] bg-[#0A0A0A] text-white px-4 py-2 border border-[#0A0A0A]" data-testid="brand-service-dashboard">
                {t("common.dashboard").toUpperCase()}
              </Link>
            ) : (
              <Link to="/register?next=/brand-service" className="font-mono-mark text-[11px] bg-[#0A0A0A] text-white px-4 py-2 border border-[#0A0A0A]" data-testid="brand-service-login-cta">
                {t("nav.start_free").toUpperCase()}
              </Link>
            )}
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="brand-service-page">
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{t("brand_service_page.breadcrumb").toUpperCase()}</div>
        <h1 className="font-display text-5xl md:text-6xl mt-3 max-w-3xl leading-none">
          {t("brand_service_page.title_a")} <span className="bg-[#FFD600] px-2 inline-block">{t("brand_service_page.title_b")}</span>{t("brand_service_page.title_c")}
        </h1>
        <p className="mt-6 max-w-xl text-[#5C5C5C] font-mono-data text-sm">
          {t("brand_service_page.intro")}
        </p>

        <div className="mt-12 grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3 space-y-6">
            <div className="bg-white border border-[#0A0A0A] brutal-shadow p-8">
              <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{t("brand_service_page.includes").toUpperCase()}</div>
              <ul className="mt-4 space-y-3 text-sm font-mono-data">
                {[
                  ["Logo principal + variantes", "PNG, SVG, AI, Figma · favicon incluido"],
                  ["3 opciones de slogan", "Multi-idioma · probados con IA + humanos"],
                  ["Paleta de colores", "Primarios · secundarios · de fondo · con códigos HEX/RGB"],
                  ["Tipografía", "Display + texto · licencia comercial"],
                  ["Guía de marca", "PDF 12 páginas · uso correcto · ejemplos"],
                  ["Brand voice", "Tono · palabras clave · palabras prohibidas · 3 ejemplos"],
                  ["Página web básica", "Landing de 1 página · responsive · hosting incluido 1er año"],
                  ["2 rondas de revisión", "Incluidas sin costo"],
                  ["1 mes Plan Pro", "Gratis · para empezar a publicar el mismo día"],
                ].map(([title, d]) => (
                  <li key={title} className="flex gap-3">
                    <Check className="w-4 h-4 text-[#E53935] shrink-0 mt-0.5" />
                    <div>
                      <div className="font-display text-base">{tr(title)}</div>
                      <div className="text-xs text-[#5C5C5C]">{tr(d)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* E-commerce add-on */}
            <div className={`border-2 ${withEcom ? "border-[#E53935] bg-[#FFD600]" : "border-[#0A0A0A] bg-white"} p-6 transition`} data-testid="ecom-addon">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4" />
                    <span className="font-mono-mark text-[10px] text-[#0A0A0A]">{t("brand_service_page.ecom_addon_mark").toUpperCase()} · +$29 USD</span>
                  </div>
                  <div className="font-display text-2xl mt-2">{t("brand_service_page.ecom_addon_title")}</div>
                  <p className="mt-2 font-mono-data text-sm text-[#0A0A0A]/80 max-w-md">
                    {t("brand_service_page.ecom_addon_desc")}
                  </p>
                </div>
                <label className="flex items-center gap-2 shrink-0 cursor-pointer" data-testid="ecom-toggle-label">
                  <input
                    type="checkbox"
                    checked={withEcom}
                    onChange={(e) => setWithEcom(e.target.checked)}
                    className="w-5 h-5 accent-[#E53935]"
                    data-testid="ecom-toggle"
                  />
                  <span className="font-mono-mark text-[10px]">{t("brand_service_page.ecom_addon_toggle").toUpperCase()}</span>
                </label>
              </div>
            </div>
          </div>

          <div className="md:col-span-2 bg-[#0A0A0A] text-white border border-[#0A0A0A] brutal-shadow p-8 flex flex-col h-fit sticky top-20">
            <div className="font-mono-mark text-[10px] text-[#FFD600]">{t("brand_service_page.quote_mark").toUpperCase()}</div>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="font-display text-7xl leading-none transition-all" data-testid="brand-service-price">{format(priceUsd)}</span>
              <span className="font-mono-mark text-[10px]">USD</span>
            </div>
            <div className="mt-2 font-mono-data text-xs text-white/60">
              {withEcom ? (
                <>Branding {format(299)} + E-commerce {format(29)}</>
              ) : (
                <><s>{format(890)}</s> · {t("brand_service_page.launch_sub")}</>
              )}
            </div>
            <div className="mt-6 pt-4 border-t border-white/20 font-mono-data text-xs flex-1">
              {tr("Pago único. Sin suscripción. Entrega garantizada en 72h hábiles. Si no te gusta ninguna propuesta, devolvemos el 100%.")}
            </div>
            <button
              onClick={() => {
                if (!user) {
                  toast.info(t("brand_service_page.login_required"));
                  navigate("/register?next=/brand-service");
                  return;
                }
                start(sku);
              }}
              disabled={checkoutLoading === sku}
              className="mt-6 inline-flex items-center justify-center gap-3 bg-[#FFD600] text-[#0A0A0A] px-6 py-4 font-mono-mark text-xs border border-[#FFD600] hover-brutal disabled:opacity-60"
              data-testid="brand-service-buy"
            >
              {checkoutLoading === sku ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t("brand_service_page.buy_cta_loading").toUpperCase()}</>
              ) : (
                <>{t("brand_service_page.buy_cta").toUpperCase()} <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
            <div className="mt-3 font-mono-mark text-[9px] text-white/50 text-center">
              {t("brand_service_page.secure_note").toUpperCase()}
            </div>
          </div>
        </div>

        {/* Existing orders */}
        {orders.length > 0 && (
          <div className="mt-16" data-testid="brand-service-orders">
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{t("brand_service_page.orders_title").toUpperCase()}</div>
            <div className="mt-4 space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="bg-white border border-[#0A0A0A] p-4 flex items-center justify-between" data-testid={`brand-order-${o.id}`}>
                  <div>
                    <div className="font-mono-mark text-[10px] text-[#5C5C5C]">#{o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleDateString("es-MX")}</div>
                    <div className="font-display text-lg">
                      {o.sku === "brand_service_ecom" ? "Branding + E-commerce" : "Servicio de Branding"} · ${o.amount} {o.currency.toUpperCase()}
                    </div>
                  </div>
                  <span className={`font-mono-mark text-[10px] px-3 py-1 ${
                    o.fulfillment_status === "delivered" ? "bg-[#0A0A0A] text-[#FFD600]"
                    : o.fulfillment_status === "in_progress" ? "bg-[#FFD600] text-[#0A0A0A]"
                    : "bg-[#E53935] text-white"
                  }`}>
                    {o.fulfillment_status === "delivered" ? "ENTREGADO"
                    : o.fulfillment_status === "in_progress" ? "EN PROCESO · 72H"
                    : "PAGADO · PENDIENTE KICK-OFF"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
